import React, { useState, useEffect, useCallback } from "react";
import {
    Card, CardHeader, Container, Row, Col, Table, Badge,
    CardFooter, Pagination, PaginationItem, PaginationLink,
    Input, Button, Modal, ModalHeader, ModalBody,
    ModalFooter, ListGroup, ListGroupItem, CustomInput, Form, FormGroup, Label
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader";
import { getAllRoles, getRoleById, deleteRole, createRole, updateRole, getAllPermissions, getAllActionPermissions, getAllRoomTypes } from "../../api/ApiServices";
import Swal from "sweetalert2";
import { debounce } from "lodash"; // You may need to install lodash or implement a simple debounce

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
    },
    actionBtn: {
        borderRadius: "8px",
        padding: "8px 16px",
        fontWeight: 600,
        letterSpacing: "0.5px"
    }
};

const RoleManagement = ({ showHeader = true, isActive = true }) => {
    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPagesCount = Math.ceil(totalItems / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2;

        pages.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (currentPage < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={currentPage === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const [roles, setRoles] = useState([]);
    const [pagination, setPagination] = useState({
        total: 0, page: 1, limit: 10, totalPages: 1,
    });
    const [search, setSearch] = useState("");
    const [modal, setModal] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [loading, setLoading] = useState(true);

    // ✅ New Modal States for Add/Edit Role
    const [roleModal, setRoleModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [allPermissions, setAllPermissions] = useState([]);
    const [allActionPermissions, setAllActionPermissions] = useState([]);
    const [allRoomTypes, setAllRoomTypes] = useState([]);
    const [roleFormData, setRoleFormData] = useState({
        r_name: "",
        r_permissions: [],
        r_action_permissions: [],
        r_room_type: ""
    });

    // ✅ Debounced search function to avoid too many API calls
    const debouncedSearch = useCallback(
        debounce((searchTerm) => {
            fetchRoles(1, pagination.limit, searchTerm);
        }, 500), // 500ms delay
        []
    );

    // ✅ Fetch roles with pagination + search
    const fetchRoles = async (page = 1, limit = 10, searchTerm = "") => {
        try {
            setLoading(true);
            const data = await getAllRoles(page, limit, searchTerm);
            // Backend sends { roles, total, page, totalPages } in payload
            const { roles = [], total = 0, totalPages = 1 } = data.payload || {};
            setRoles(roles);
            setPagination({ total, page, limit, totalPages });
        } catch (error) {
            console.error("Failed to fetch roles:", error);
            Swal.fire("Error!", "Failed to fetch roles", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPermissions();
        fetchRoomTypes();
    }, []);

    const fetchRoomTypes = async () => {
        try {
            const data = await getAllRoomTypes(1, 100, "", "1"); // Fetch active types
            setAllRoomTypes(data?.payload?.roomTypes || []);
        } catch (error) {
            console.error("Failed to fetch room types:", error);
        }
    };

    useEffect(() => {
        if (isActive) fetchRoles(pagination.page, pagination.limit, search);
    }, [isActive, pagination.page, search]);

    const fetchPermissions = async () => {
        try {
            const [permData, actionData] = await Promise.all([
                getAllPermissions(),
                getAllActionPermissions()
            ]);
            setAllPermissions(permData?.payload?.permissions || []);
            setAllActionPermissions(actionData?.payload?.actionPermissions || []);
        } catch (error) {
            console.error("Failed to fetch permissions:", error);
        }
    };

    // ✅ Open modal for adding a new role
    const handleAddRole = () => {
        setEditingRole(null);
        setRoleFormData({ r_name: "", r_permissions: [], r_action_permissions: [], r_room_type: "" });
        setRoleModal(true);
    };

    // ✅ Open modal for editing an existing role
    const handleEditRole = async (role) => {
        try {
            const data = await getRoleById(role.r_uuid);
            const r = data?.payload?.role || role; // Adjusted to match backend payload structure
            setEditingRole(r);
            setRoleFormData({
                r_name: r.r_name,
                r_permissions: r.r_permissions?.map(p => typeof p === 'string' ? p : p.p_uuid) || [],
                r_action_permissions: r.r_action_permissions || [],
                r_room_type: r.r_room_type || ""
            });
            setRoleModal(true);
        } catch (error) {
            Swal.fire("Error!", "Failed to fetch role details", "error");
        }
    };

    // ✅ Handle permission toggle in the modal
    const handlePermissionToggle = (uuid) => {
        setRoleFormData(prev => {
            const newPermissions = [...prev.r_permissions];
            if (newPermissions.includes(uuid)) {
                return { ...prev, r_permissions: newPermissions.filter(p => p !== uuid) };
            } else {
                return { ...prev, r_permissions: [...newPermissions, uuid] };
            }
        });
    };

    // ✅ Handle action permission toggle
    const handleActionToggle = (id) => {
        setRoleFormData(prev => {
            const newActions = [...prev.r_action_permissions];
            if (newActions.includes(id)) {
                return { ...prev, r_action_permissions: newActions.filter(a => a !== id) };
            } else {
                return { ...prev, r_action_permissions: [...newActions, id] };
            }
        });
    };


    const handleSaveRole = async () => {
        if (!roleFormData.r_name.trim()) {
            Swal.fire({
                title: "Warning!",
                text: "Please provide a role name",
                icon: "warning",
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (
            roleFormData.r_permissions.length === 0 &&
            roleFormData.r_action_permissions.length === 0
        ) {
            Swal.fire({
                title: "Warning!",
                text: "Please assign at least one module or action permission",
                icon: "warning",
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        try {
            if (editingRole) {
                await updateRole(editingRole.r_uuid, roleFormData);

                Swal.fire({
                    title: "Updated!",
                    text: "Role permissions adjusted.",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000
                });

            } else {
                await createRole(roleFormData);

                Swal.fire({
                    title: "Success!",
                    text: "New role defined successfully.",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000
                });
            }

            setRoleModal(false);
            fetchRoles(pagination.page, pagination.limit, search);

        } catch (error) {
            Swal.fire({
                title: "Error!",
                text: error.message || "Failed to save role",
                icon: "error",
                showConfirmButton: false,
                timer: 2000
            });
        }
    };

    // ✅ Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSearch(value);
    };

    // ✅ Clear search (Unused but kept for utility)
    const handleClearSearch = () => {
        setSearch("");
        fetchRoles(1, pagination.limit, "");
    };

    // ✅ Delete role
    const handleDelete = async (_id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "All admins assigned to this role will lose module access until reassigned.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (result.isConfirmed) {
            try {
                await deleteRole(_id);
                Swal.fire({ title: "Deleted!", text: "Role deleted successfully!", icon: "success", timer: 3000, showConfirmButton: false });
                fetchRoles(pagination.page, pagination.limit, search);
            } catch (error) {
                console.error("Failed to delete role:", error);
                const msg = error?.response?.data?.message || "Failed to delete role";
                Swal.fire("Cannot Delete!", msg, "warning");
            }
        }
    };

    // ✅ Open modal with permissions
    // ✅ Open modal with permissions
    const toggleModal = async (id = null) => {
        if (id) {
            try {
                const data = await getRoleById(id);
                console.log("Role details response:", data); // Debug log

                // Check the actual response structure
                // Based on your other API calls, it might be data.payload.role or data.role
                const roleData = data?.payload?.role || data?.role || data;

                setSelectedRole(roleData);
                // Get permissions - they might be objects or strings
                const permissions = roleData?.r_permissions || [];
                setSelectedPermissions(permissions);

                setModal(true);
            } catch (error) {
                console.error("Failed to fetch role details:", error);
                Swal.fire("Error!", "Failed to fetch permissions", "error");
            }
        } else {
            setSelectedRole(null);
            setSelectedPermissions([]);
            setModal(false);
        }
    };


    return (
        <>
            {showHeader && <CommonHeader />}
            <Container className={showHeader ? "mt--7" : "mt-0"} fluid>
                <Row className="mb-4">
                    <Col className="d-flex justify-content-between align-items-center flex-wrap">
                        <h2 className={showHeader ? "text-white mb-0" : "text-dark mb-0"}>Role & Permission Matrix</h2>
                        <Button color={showHeader ? "secondary" : "primary"} style={styles.actionBtn} onClick={handleAddRole}>
                            <i className="fas fa-plus-circle mr-2" /> Define New Role
                        </Button>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Card className="shadow" style={styles.glassCard}>
                            <CardHeader className="border-0 bg-transparent py-4">
                                <Row className="align-items-center">
                                    <Col>
                                        <h3 className="mb-0 text-dark font-weight-700">
                                            <i className="ni ni-single-02 text-info mr-2" /> Available Roles
                                        </h3>
                                        <p className="text-muted text-sm mb-0">Define organizational roles and assign granular module permissions.</p>
                                    </Col>
                                    <Col md="4" className="mt-3 mt-md-0">
                                        <Input
                                            placeholder="Search roles by name..."
                                            type="text"
                                            style={{ borderRadius: "8px" }}
                                            value={search}
                                            onChange={handleSearchChange}
                                        />
                                    </Col>
                                </Row>
                            </CardHeader>
                            <div className="table-responsive">
                                <Table className="align-items-center table-flush" hover>
                                    <thead className="thead-light">
                                        <tr>
                                            <th scope="col" style={{ padding: "16px 24px" }}>Sr.No</th>
                                            <th scope="col">Role Specification</th>
                                            <th scope="col" className="text-center">Permissions Count</th>
                                            <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="4" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td>
                                            </tr>
                                        ) : roles.length > 0 ? (
                                            roles.map((role, index) => (
                                                <tr key={role.r_uuid}>
                                                    <td style={{ padding: "16px 24px" }}>{index + 1 + (pagination.page - 1) * pagination.limit}</td>
                                                    <td>
                                                        <span className="text-dark font-weight-700 mb-0 d-block">{role.r_name}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge color="info" pill style={styles.moduleBadge}>
                                                            {role.r_permissions?.length || 0} Modules
                                                        </Badge>
                                                    </td>
                                                    <td className="text-right" style={{ paddingRight: "24px" }}>
                                                        <Button color="info" outline size="sm" className="mr-2" onClick={() => toggleModal(role.r_uuid)}>View</Button>
                                                        <Button color="warning" outline size="sm" className="mr-2" onClick={() => handleEditRole(role)}>Edit</Button>
                                                        <Button 
                                                            color="danger" 
                                                            outline 
                                                            size="sm" 
                                                            onClick={() => handleDelete(role.r_uuid)}
                                                            disabled={["admin", "judge", "moderator", "sen. moderator"].includes(role.r_name?.trim().toLowerCase())}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-4 text-muted">No roles found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            {/*  Dynamic Pagination */}
                            {roles.length > 0 && (
                                <CardFooter className="py-4 bg-transparent border-top d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">
                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} roles
                                    </span>
                                    {pagination.totalPages > 1 && (
                                        <nav aria-label="...">
                                            <Pagination className="pagination justify-content-end mb-0" listClassName="justify-content-end mb-0">
                                                <PaginationItem disabled={pagination.page === 1}>
                                                    <PaginationLink
                                                        href="#pablo"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (pagination.page > 1) {
                                                                fetchRoles(pagination.page - 1, pagination.limit, search);
                                                            }
                                                        }}
                                                        tabIndex="-1"
                                                    >
                                                        <i className="fas fa-angle-left" />
                                                    </PaginationLink>
                                                </PaginationItem>

                                                {renderPaginationItems(pagination.page, pagination.total, pagination.limit, (page) => fetchRoles(page, pagination.limit, search))}

                                                <PaginationItem disabled={pagination.page === pagination.totalPages}>
                                                    <PaginationLink
                                                        href="#pablo"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (pagination.page < pagination.totalPages) {
                                                                fetchRoles(pagination.page + 1, pagination.limit, search);
                                                            }
                                                        }}
                                                    >
                                                        <i className="fas fa-angle-right" />
                                                    </PaginationLink>
                                                </PaginationItem>
                                            </Pagination>
                                        </nav>
                                    )}
                                </CardFooter>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Permissions Modal */}
            {/* Permissions Modal */}
            <Modal isOpen={modal} toggle={() => toggleModal(null)} centered size="lg" style={{ borderRadius: "16px", overflow: "hidden" }}>
                <ModalHeader toggle={() => toggleModal(null)} className="border-0">
                    <span className="text-uppercase text-muted ls-1 font-weight-bold">
                        Permission Manifest - {selectedRole?.r_name || "Role"}
                    </span>
                </ModalHeader>
                <ModalBody style={{ maxHeight: "60vh", overflowY: "auto" }} className="pt-0">
                    <div className="bg-secondary p-4" style={{ borderRadius: "12px" }}>
                        <h6 className="mb-3" style={{ fontSize: "0.75rem", fontWeight: "700", color: "#8898aa", textTransform: "uppercase" }}>
                            <i className="fas fa-key mr-2" /> Module Permissions
                        </h6>
                        {selectedPermissions?.length > 0 ? (
                            <Row>
                                {selectedPermissions.map((perm, idx) => {

                                    // ✅ Get proper permission name
                                    const permissionName =
                                        typeof perm === "object"
                                            ? (perm.p_name || perm.name)
                                            : (
                                                allPermissions.find(
                                                    (p) =>
                                                        p.p_uuid === perm ||
                                                        p.p_id === perm ||
                                                        p._id === perm
                                                )?.p_name
                                            );

                                    return (
                                        <Col md="6" key={idx} className="mb-3">
                                            <div
                                                className="d-flex align-items-center px-3 py-2"
                                                style={{
                                                    background: "#fff",
                                                    borderRadius: "10px",
                                                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                                                    minHeight: "50px"
                                                }}
                                            >
                                                <div
                                                    className="d-flex align-items-center justify-content-center mr-3"
                                                    style={{
                                                        width: "28px",
                                                        height: "28px",
                                                        borderRadius: "50%",
                                                        background: "rgba(45, 206, 137, 0.15)"
                                                    }}
                                                >
                                                    <i className="fas fa-check text-success" style={{ fontSize: "12px" }} />
                                                </div>

                                                <span
                                                    className="text-dark"
                                                    style={{
                                                        fontWeight: 600,
                                                        fontSize: "14px",
                                                        wordBreak: "break-word"
                                                    }}
                                                >
                                                    {permissionName || "Unknown Permission"}
                                                </span>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        ) : (
                            <p className="text-muted text-center py-3 mb-0">
                                <i className="fas fa-info-circle mr-2" />
                                No specific module permissions assigned to this role.
                            </p>
                        )}
                    </div>

                    {/* Display Action Permissions if they exist */}
                    {selectedRole?.r_action_permissions && selectedRole.r_action_permissions.length > 0 && (
                        <div className="bg-secondary p-4 mt-3" style={{ borderRadius: "12px" }}>
                            {/* <h6 className="mb-3" style={{ fontSize: "0.75rem", fontWeight: "700", color: "#8898aa", textTransform: "uppercase" }}>
                                <i className="fas fa-bolt mr-2" /> Action Permissions
                            </h6> */}
                            {/* <Row>
                                {selectedRole.r_action_permissions.map((action, idx) => (
                                    <Col md="6" key={idx} className="mb-2">
                                        <div className="d-flex align-items-center">
                                            <i className="fas fa-check-circle text-info mr-2" />
                                            <span className="text-dark font-weight-600">
                                                {typeof action === "object" ? (action.ap_name || action.name || "Unknown") : action}
                                            </span>
                                        </div>
                                    </Col>
                                ))}
                            </Row> */}
                        </div>
                    )}

                    {/* Display Room Type if assigned */}
                    {selectedRole?.r_room_type && (
                        <div className="bg-secondary p-4 mt-3" style={{ borderRadius: "12px" }}>
                            <h6 className="mb-3" style={{ fontSize: "0.75rem", fontWeight: "700", color: "#8898aa", textTransform: "uppercase" }}>
                                <i className="fas fa-building mr-2" /> Associated Room Type
                            </h6>
                            <div className="d-flex align-items-center">
                                <i className="fas fa-door-open text-primary mr-2" />
                                <span className="text-dark font-weight-600">
                                    {selectedRole.r_room_type_name || selectedRole.r_room_type || "Not assigned"}
                                </span>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button color="secondary" outline onClick={() => toggleModal(null)} style={{ borderRadius: "8px" }}>
                        <i className="fas fa-times mr-2" /> Dismiss
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Role Add/Edit Modal */}
            <Modal isOpen={roleModal} toggle={() => setRoleModal(false)} centered size="lg" style={{ borderRadius: "16px", overflow: "hidden" }}>
                <ModalHeader toggle={() => setRoleModal(false)} className="border-0">
                    <span className="text-uppercase text-muted ls-1 font-weight-bold">
                        {editingRole ? "Modify Role" : "Define New Role"}
                    </span>
                </ModalHeader>
                <ModalBody className="pt-0">
                    <Form>
                        <FormGroup>
                            <Label className="form-control-label" for="r_name">Role Designation</Label>
                            <Input
                                type="text"
                                id="r_name"
                                placeholder="e.g. Operations Manager"
                                value={roleFormData.r_name}
                                style={{ borderRadius: "10px", height: "45px" }}
                                onChange={(e) => setRoleFormData({ ...roleFormData, r_name: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label className="form-control-label" for="r_room_type">Associated Room Type</Label>
                            <Input
                                type="select"
                                id="r_room_type"
                                value={roleFormData.r_room_type}
                                style={{ borderRadius: "10px", height: "45px" }}
                                onChange={(e) => setRoleFormData({ ...roleFormData, r_room_type: e.target.value })}
                            >
                                <option value="">Select Room Type (Optional)</option>
                                {allRoomTypes.map((rt) => (
                                    <option key={rt.rt_uuid} value={rt.rt_uuid}>
                                        {rt.rt_name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <h6 className="mt-4 mb-3" style={{ fontSize: "0.75rem", fontWeight: "700", color: "#8898aa", textTransform: "uppercase" }}>
                            Assign System Modules
                        </h6>
                        <div className="bg-secondary p-4 mb-2" style={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.05)", maxHeight: "30vh", overflowY: "auto" }}>
                            <Row>
                                {allPermissions.map((perm, idx) => (
                                    <Col md="6" key={idx} className="mb-2">
                                        <CustomInput
                                            type="switch"
                                            id={`perm-${perm.p_uuid}`}
                                            label={perm.p_name}
                                            checked={roleFormData.r_permissions.includes(perm.p_uuid)}
                                            onChange={() => handlePermissionToggle(perm.p_uuid)}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </div>

                        <h6 className="mt-4 mb-3" style={{ fontSize: "0.75rem", fontWeight: "700", color: "#8898aa", textTransform: "uppercase" }}>
                            System Action Permissions
                        </h6>
                        <div className="bg-lighter p-4 mb-2" style={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.05)", maxHeight: "30vh", overflowY: "auto" }}>
                            <Row>
                                {allActionPermissions.map((action, idx) => (
                                    <Col md="6" key={idx} className="mb-2">
                                        <CustomInput
                                            type="switch"
                                            id={`action-${action._id}`}
                                            label={action.ap_name}
                                            checked={roleFormData.r_action_permissions.includes(action._id)}
                                            onChange={() => handleActionToggle(action._id)}
                                        />
                                    </Col>
                                ))}
                                {allActionPermissions.length === 0 && (
                                    <Col className="text-center text-muted py-2">No action permissions available.</Col>
                                )}
                            </Row>
                        </div>
                    </Form>
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button color="secondary" outline onClick={() => setRoleModal(false)} style={{ borderRadius: "8px" }}>Cancel</Button>
                    <Button color="primary" onClick={handleSaveRole} style={{ borderRadius: "8px", padding: "8px 24px" }}>
                        {editingRole ? "Update Role" : "Create Role"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default RoleManagement;